import { supabase } from '../lib/supabaseClient'

const ALLOWED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'application/pdf',
]

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const MAX_FILES = 10

export interface ValidationError {
  field: string
  message: string
}

export function validateFiles(files: File[]): ValidationError[] {
  const errors: ValidationError[] = []

  if (files.length === 0) {
    errors.push({ field: 'files', message: 'Please select at least one file' })
    return errors
  }

  if (files.length > MAX_FILES) {
    errors.push({
      field: 'files',
      message: `Maximum ${MAX_FILES} files allowed`,
    })
  }

  files.forEach((file, index) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      errors.push({
        field: `file-${index}`,
        message: `${file.name}: Invalid file type. Only images and PDFs are allowed`,
      })
    }

    if (file.size > MAX_FILE_SIZE) {
      errors.push({
        field: `file-${index}`,
        message: `${file.name}: File size exceeds 5MB limit`,
      })
    }
  })

  return errors
}

export async function uploadFiles(
  files: File[],
  userId: string,
  onProgress?: (fileName: string, progress: number) => void,
): Promise<string[]> {
  const uploadedUrls: string[] = []

  for (const file of files) {
    const fileExt = file.name.split('.').pop()
    const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`

    const { data, error } = await supabase.storage
      .from('screenshots')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      })

    if (error) {
      throw new Error(`Failed to upload ${file.name}: ${error.message}`)
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from('screenshots').getPublicUrl(fileName)

    uploadedUrls.push(publicUrl)

    if (onProgress) {
      onProgress(file.name, 100)
    }
  }

  return uploadedUrls
}

// The scans table is read-only from the client. Status, score, risk level,
// analysis and unlock state are all written by the backend.

// Trigger async AI analysis (fire-and-forget pattern)
export async function triggerAsyncAnalysis(
  scanId: string,
  fileUrls: string[],
  userId: string,
  extractedText?: string,
  mode: string = 'default',
): Promise<{ success: boolean; allowed?: boolean; reason?: string }> {
  try {
    console.log('[EDGE FUNCTION] Calling analyze_toxicity for scan:', scanId)

    // Create a timeout promise that resolves after 10 seconds
    // This prevents waiting indefinitely for the Edge Function
    const timeoutPromise = new Promise<{ data: any; error: any }>((resolve) => {
      setTimeout(() => {
        console.log('[EDGE FUNCTION] Call timed out - processing in background')
        resolve({ data: { success: true, background: true }, error: null })
      }, 10000)
    })

    // Create the Edge Function call promise
    // CRITICAL: Send ALL file URLs so the backend analyzes every screenshot,
    // not just the first one. Keep `screenshot_url` for backward compatibility.
    const edgeFunctionPromise = supabase.functions.invoke('analyze_toxicity', {
      body: {
        scan_id: scanId,
        user_id: userId,
        screenshot_url: fileUrls[0] || '',
        screenshot_urls: fileUrls,
        conversation_text: extractedText || '',
        mode: mode,
      },
    })

    // Race between timeout and Edge Function call
    let result: { data: any; error: any }

    try {
      result = await Promise.race([edgeFunctionPromise, timeoutPromise])
    } catch (raceError: any) {
      console.error('[EDGE FUNCTION] Race error:', raceError)
      // If race fails, assume background processing
      result = { data: { success: true, background: true }, error: null }
    }

    const { data, error } = result

    if (error) {
      console.error('[EDGE FUNCTION] Error:', error)

      // Check for specific error types
      const errorMessage = error.message || String(error)
      const isServerError =
        errorMessage.includes('520') ||
        errorMessage.includes('502') ||
        errorMessage.includes('503') ||
        errorMessage.includes('504') ||
        errorMessage.includes('Failed to send')

      if (isServerError) {
        console.log(
          '[EDGE FUNCTION] Server error detected - scan will process in background',
        )
        // Don't throw - let the scan process in background
        // The realtime subscription will pick up when it completes
        return { success: true, allowed: true }
      }

      throw new Error(errorMessage)
    }

    console.log('[EDGE FUNCTION] Response data:', data)

    // DEDUPLICATION: If the Edge Function returns "Already active",
    // don't treat it as an error — the scan is already running
    if (
      data?.status === 'Already active' ||
      data?.message === 'Already active'
    ) {
      console.log(
        '[EDGE FUNCTION] Scan already active — deduplication hit, not an error',
      )
      return { success: true, allowed: true }
    }

    // CRITICAL: Check if backend returned "not allowed" response
    if (data?.allowed === false) {
      console.log('[EDGE FUNCTION] Backend blocked scan:', data.reason)
      return {
        success: false,
        allowed: false,
        reason: data.reason || 'NO_CREDITS',
      }
    }

    // Results (sync or async) are written to the scan row by the backend and
    // arrive through the realtime subscription.
    console.log('[EDGE FUNCTION] Accepted — results will arrive via realtime')
    return { success: true, allowed: true }
  } catch (error: any) {
    console.error('[EDGE FUNCTION] Async analysis error:', error)

    // Check if it's a server error (520, 502, 503, 504)
    const errorMessage = error.message || String(error)
    const isServerError =
      errorMessage.includes('520') ||
      errorMessage.includes('502') ||
      errorMessage.includes('503') ||
      errorMessage.includes('504') ||
      errorMessage.includes('Failed to send')

    if (isServerError) {
      console.log(
        '[EDGE FUNCTION] Server overload - scan will process when server recovers',
      )
      // Don't update to failed - let it stay in pending/processing
      // The backend will retry automatically
      throw new Error('SERVER_OVERLOAD')
    }

    throw error
  }
}

// Real AI analysis using Edge Function (SYNCHRONOUS - waits for complete results)
export async function analyzeScan(
  fileUrls: string[],
  userId: string,
  extractedText?: string,
  mode: string = 'default',
): Promise<{
  score: number
  risk_level: 'low' | 'medium' | 'high'
  message_count: number
  patterns: any[]
}> {
  try {
    console.log('Starting analysis:', {
      fileUrls: fileUrls.length,
      hasText: !!extractedText,
      mode,
    })

    // Call Edge Function with 90-second timeout (before Cloudflare's 100s limit)
    // This prevents the 524 timeout error
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 90000) // 90 seconds

    try {
      const { data, error } = await supabase.functions.invoke(
        'analyze_toxicity',
        {
          body: {
            user_id: userId,
            screenshot_url: fileUrls[0] || '',
            screenshot_urls: fileUrls,
            conversation_text: extractedText || '',
            mode: mode,
          },
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )

      clearTimeout(timeoutId)

      if (error) {
        console.error('Edge Function error:', error)
        throw new Error(`Analysis service error: ${error.message}`)
      }

      console.log('Edge Function response data:', data)

      if (!data.success) {
        throw new Error(data.error || 'Analysis failed - no success flag')
      }

      if (!data.result) {
        throw new Error('Analysis failed - no result data returned')
      }

      // Transform Edge Function response to match expected format
      const result = data.result
      const messages = result.messages || []

      console.log('Processing messages:', messages.length)

      // Calculate overall score (average severity)
      const avgSeverity =
        messages.length > 0
          ? messages.reduce(
              (sum: number, msg: any) => sum + (msg.severity || 0),
              0,
            ) / messages.length
          : 0

      const score = Math.round(avgSeverity)
      const risk_level: 'low' | 'medium' | 'high' =
        score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low'

      // Group messages by flag types to create patterns
      const patternMap = new Map<string, any>()

      messages.forEach((msg: any) => {
        msg.flags?.forEach((flag: string) => {
          if (!patternMap.has(flag)) {
            patternMap.set(flag, {
              type: flag,
              severity:
                msg.severity >= 70
                  ? 'HIGH'
                  : msg.severity >= 40
                    ? 'MEDIUM'
                    : 'LOW',
              count: 0,
              description: msg.short_reason || `${flag} pattern detected`,
              examples: [],
            })
          }
          const pattern = patternMap.get(flag)
          pattern.count++
          if (pattern.examples.length < 3) {
            pattern.examples.push(msg.text)
          }
        })
      })

      const patterns = Array.from(patternMap.values())

      console.log('Analysis complete:', {
        score,
        risk_level,
        message_count: messages.length,
        patterns: patterns.length,
      })

      return {
        score,
        risk_level,
        message_count: messages.length,
        patterns,
      }
    } catch (abortError: any) {
      clearTimeout(timeoutId)
      if (abortError.name === 'AbortError') {
        throw new Error(
          'Analysis is taking longer than expected. The system will continue processing in the background. Please check back in a few minutes or try with a shorter conversation.',
        )
      }
      throw abortError
    }
  } catch (error: any) {
    console.error('Analysis error details:', error)

    // Provide user-friendly error messages
    if (error.message.includes('taking longer than expected')) {
      throw error // Pass through timeout message
    }

    throw new Error(`Failed to analyze: ${error.message}`)
  }
}

// Store analysis result in database and send email
export async function storeAnalysisResult(
  userId: string,
  screenshotUrl: string,
  resultJson: any,
  scanTitle: string,
  toxicityScore: number,
): Promise<string> {
  const { data, error } = await supabase
    .from('analysis_results')
    .insert({
      user_id: userId,
      screenshot_url: screenshotUrl,
      result_json: resultJson,
      created_at: new Date().toISOString(),
    })
    .select()
    .single()

  if (error) throw error

  // Email notifications (toxicityReport, accountActivity) are handled
  // automatically by the database trigger when a scan result is stored.
  // No frontend email send needed here.

  return data.id
}
